import { useCallback, useState } from "react";
import { useQuery, useMutation } from "@apollo/client/react";
import {
  type AddPassengerInput,
  useEventsAdminDemo,
} from "@app/lib/events-admin-demo-context";
import {
  GET_EVENT_DETAILS,
  ADD_PASSENGER,
  EDIT_GROUP,
} from "@app/graphql/events-admin.queries";
import {
  ArrowLeftIcon,
  CalendarIcon,
  CloseIcon,
  DownloadIcon,
  PinIcon,
  PlusIcon,
} from "@app/lib/events-admin-icons";
import { formatDateRange, formatShortDate } from "@app/lib/events-admin-utils";
import { Controller } from "react-hook-form";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { z } from "zod";
import {
  Dialog,
  Modal,
  ModalOverlay,
  Button,
  ValidatedForm,
  InputBase,
  ValidatedInput,
  Select,
  TextAreaBase,
  Toggle,
} from "@its-engineering-and-development/ui-foundation/components";
import {
  createFormSchema,
  schemas,
  useValidatedForm,
} from "@its-engineering-and-development/ui-foundation/utils";
import { PlacesAutocompleteInput } from "@app/components/places-autocomplete-input";

const editGroupSchema = createFormSchema({
  groupName: schemas.sanitizedRequiredString,
  groupType: z.enum(["Corporate", "Wedding", "Sports", "Summit", "Conference"]),
  destination: schemas.sanitizedRequiredString,
  organizerName: schemas.sanitizedRequiredString,
  organizerEmail: schemas.email,
  venueName: schemas.sanitizedRequiredString,
  venueAddress: schemas.sanitizedRequiredString,
  departDate: schemas.dateString,
  returnDate: schemas.dateString,
  passengers: z.coerce.number().int().positive("Must be at least 1"),
  paymentModel: z.enum(["Organizer Pays", "Traveler Pays"]),
});

const addPassengerSchema = createFormSchema({
  name: schemas.sanitizedRequiredString,
  airline: schemas.sanitizedRequiredString,
  locator: schemas.sanitizedRequiredString,
  ticket: schemas.sanitizedRequiredString,
  amountPaid: schemas.sanitizedRequiredString,
});

const emailOrganizerSchema = createFormSchema({
  to: schemas.email,
  subject: schemas.sanitizedRequiredString,
  body: schemas.sanitizedRequiredString,
});

function parseCsvPassengers(csvText: string): AddPassengerInput[] {
  const lines = csvText.trim().split("\n");
  if (lines.length < 2) return [];
  return lines
    .slice(1)
    .map((line) => {
      const cols = line.split(",").map((c) => c.trim());
      return {
        name: cols[0] ?? "",
        airline: cols[2] ?? "",
        locator: cols[3] ?? "",
        ticket: cols[4] ?? "",
        amountPaid: cols[5] ?? "",
      };
    })
    .filter((p) => p.name.length > 0);
}

interface EventDetailsQueryData {
  eventDetails: {
    event: {
      id: string;
      groupName: string;
      groupType: string;
      organizerName: string;
      organizerEmail: string;
      destination: string;
      departDate: string;
      returnDate: string;
      passengers: number;
    };
    metrics: {
      bookedCount: number;
      paymentModel: string;
    };
  };
  passengerManifest: Array<{
    id: string;
    name: string;
    number: number;
    airline: string;
    locator: string;
    ticket: string;
    amountPaid: string;
  }>;
  agreement: { fileName: string; uploadedAt: string; status: string } | null;
}

interface AddPassengerMutationData {
  addPassenger: { id: string };
}

interface EditGroupMutationData {
  editGroup: { id: string };
}

export function GroupDetailPage() {
  const { groupId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    getEventById,
    addPassenger: addPassengerLocal,
    editGroup: editGroupLocal,
    bulkUploadPassengers,
  } = useEventsAdminDemo();

  // GraphQL: fetch event details from the mock server
  const {
    data: gqlData,
    loading: gqlLoading,
    refetch,
  } = useQuery<EventDetailsQueryData>(GET_EVENT_DETAILS, {
    variables: { eventId: groupId },
    skip: !groupId,
    fetchPolicy: "network-only",
  });

  const [addPassengerMutation] =
    useMutation<AddPassengerMutationData>(ADD_PASSENGER);
  const [editGroupMutation] = useMutation<EditGroupMutationData>(EDIT_GROUP);

  // Use GraphQL data if available, fall back to demo context
  const gqlEvent = gqlData?.eventDetails?.event;
  const gqlMetrics = gqlData?.eventDetails?.metrics;
  const localEvent = groupId ? getEventById(groupId) : undefined;
  const eventItem = gqlEvent
    ? {
        ...localEvent,
        ...gqlEvent,
        bookedCount: gqlMetrics?.bookedCount ?? localEvent?.bookedCount ?? 0,
        paymentModel:
          gqlMetrics?.paymentModel ??
          localEvent?.paymentModel ??
          "Organizer Pays",
        passengerManifest:
          gqlData?.passengerManifest ?? localEvent?.passengerManifest ?? [],
        agreement: gqlData?.agreement ?? localEvent?.agreement,
        invitees: localEvent?.invitees ?? [],
        schedule: localEvent?.schedule ?? [],
        // Preserve fields that only exist in demo context
        venueName: localEvent?.venueName ?? "",
        venueAddress: localEvent?.venueAddress ?? "",
        origin: localEvent?.origin ?? "",
        airlineAccess: localEvent?.airlineAccess ?? [],
        rsvpAttendees: localEvent?.rsvpAttendees ?? [],
        slug: localEvent?.slug ?? "",
        inviteHeadline: localEvent?.inviteHeadline ?? "",
        inviteLink: localEvent?.inviteLink ?? "",
        showSchedule: localEvent?.showSchedule ?? false,
        showRsvp: localEvent?.showRsvp ?? false,
        backgroundStyle: localEvent?.backgroundStyle ?? ("lagoon" as const),
      }
    : localEvent;

  const [airlineState, setAirlineState] = useState<Record<string, boolean>>({});
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [agreementGenerated, setAgreementGenerated] = useState(false);
  const [signatureSent, setSignatureSent] = useState(false);
  const [isEditGroupOpen, setIsEditGroupOpen] = useState(false);
  const [isAddPassengerOpen, setIsAddPassengerOpen] = useState(false);
  const [isEmailOrganizerOpen, setIsEmailOrganizerOpen] = useState(false);
  const [emailSentConfirm, setEmailSentConfirm] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);

  const editGroupForm = useValidatedForm(editGroupSchema, {
    defaultValues: {
      groupName: eventItem?.groupName ?? "",
      groupType: (eventItem?.groupType ?? "Corporate") as "Corporate",
      destination: eventItem?.destination ?? "",
      organizerName: eventItem?.organizerName ?? "",
      organizerEmail: eventItem?.organizerEmail ?? "",
      venueName: eventItem?.venueName ?? "",
      venueAddress: eventItem?.venueAddress ?? "",
      departDate: eventItem?.departDate ?? "",
      returnDate: eventItem?.returnDate ?? "",
      passengers: eventItem?.passengers ?? 1,
      paymentModel: (eventItem?.paymentModel ??
        "Organizer Pays") as "Organizer Pays",
    },
  });

  const addPassengerForm = useValidatedForm(addPassengerSchema, {
    defaultValues: {
      name: "",
      airline: "",
      locator: "",
      ticket: "",
      amountPaid: "",
    },
  });

  const emailOrganizerForm = useValidatedForm(emailOrganizerSchema, {
    defaultValues: {
      to: eventItem?.organizerEmail ?? "",
      subject: `Regarding Group ${eventItem?.id ?? ""}`,
      body: "",
    },
  });

  const handleEditGroup = async (values: z.infer<typeof editGroupSchema>) => {
    if (!eventItem) return;
    // Call GraphQL mutation
    const { data } = await editGroupMutation({
      variables: { eventId: eventItem.id, ...values },
    });
    if (!data?.editGroup.id) return;
    // Also update local context for immediate UI consistency
    editGroupLocal(eventItem.id, values);
    refetch();
    setIsEditGroupOpen(false);
  };

  const handleAddPassenger = async (
    values: z.infer<typeof addPassengerSchema>,
  ) => {
    if (!eventItem) return;
    // Call GraphQL mutation
    const { data } = await addPassengerMutation({
      variables: { eventId: eventItem.id, ...values },
    });
    if (!data?.addPassenger.id) return;
    // Also update local context
    addPassengerLocal(eventItem.id, values);
    addPassengerForm.reset();
    refetch();
    setIsAddPassengerOpen(false);
  };

  const handleEmailOrganizer = (
    _values: z.infer<typeof emailOrganizerSchema>,
  ) => {
    emailOrganizerForm.reset();
    setIsEmailOrganizerOpen(false);
    setEmailSentConfirm(true);
    setTimeout(() => setEmailSentConfirm(false), 2000);
  };

  const handleOpenEditGroup = useCallback(() => {
    if (!eventItem) return;
    editGroupForm.reset({
      groupName: eventItem.groupName,
      groupType: eventItem.groupType as "Corporate",
      destination: eventItem.destination,
      organizerName: eventItem.organizerName,
      organizerEmail: eventItem.organizerEmail,
      venueName: eventItem.venueName,
      venueAddress: eventItem.venueAddress,
      departDate: eventItem.departDate,
      returnDate: eventItem.returnDate,
      passengers: eventItem.passengers,
      paymentModel: eventItem.paymentModel as "Organizer Pays",
    });
    setIsEditGroupOpen(true);
  }, [eventItem, editGroupForm]);

  const handleBulkUpload = useCallback(() => {
    if (!eventItem) return;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv";
    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const passengers = parseCsvPassengers(text);
        if (passengers.length > 0) {
          bulkUploadPassengers(eventItem.id, passengers);
          setUploadStatus(`${passengers.length} passengers uploaded`);
          setTimeout(() => setUploadStatus(null), 3000);
        } else {
          setUploadStatus("No valid rows found");
          setTimeout(() => setUploadStatus(null), 3000);
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }, [eventItem, bulkUploadPassengers]);

  const isAdminRoute = location.pathname.startsWith("/admin");
  const backTo = isAdminRoute ? "/admin/dashboard" : "/organizers";
  const backLabel = "Back to Dashboard";

  if (gqlLoading && !eventItem) {
    return (
      <section className="events-page">
        <div className="events-empty-state">
          <h1 className="events-page__title">Loading...</h1>
        </div>
      </section>
    );
  }

  if (!eventItem) {
    return (
      <section className="events-page">
        <div className="events-empty-state">
          <h1 className="events-page__title">Group not found</h1>
          <Link to={backTo} className="events-inline-link">
            Back to dashboard
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="events-page events-page--stack events-page--with-sidebar">
      <aside className="events-detail-sidebar">
        <Link to={backTo} className="events-inline-link">
          <ArrowLeftIcon className="size-4" />
          {backLabel}
        </Link>

        <div className="events-detail-sidebar__summary">
          <span className="events-detail-sidebar__label">
            Group Details Summary
          </span>
          <div className="events-detail-sidebar__group-id">
            <span className="events-detail-sidebar__label">Group ID</span>
            <strong title={eventItem.id}>{eventItem.id}</strong>
          </div>
        </div>
      </aside>

      <div className="events-detail-main">
        <header className="events-page__hero events-page__hero--status">
          <div>
            <h1 className="events-page__title">{eventItem.groupName}</h1>
            <div className="events-meta-row">
              <span>
                <PinIcon className="size-4" />
                {eventItem.destination}
              </span>
              <span>
                <CalendarIcon className="size-4" />
                {formatDateRange(eventItem.departDate, eventItem.returnDate)}
              </span>
            </div>
          </div>

          <div className="events-page__hero-actions">
            <Button
              size="lg"
              color="secondary"
              className="events-outline-button"
              onPress={handleOpenEditGroup}
            >
              Edit Group
            </Button>
            <Button
              size="lg"
              color="secondary"
              className="events-outline-button"
              onPress={() => {
                emailOrganizerForm.reset({
                  to: eventItem.organizerEmail,
                  subject: `Regarding Group ${eventItem.id}`,
                  body: "",
                });
                setIsEmailOrganizerOpen(true);
              }}
            >
              {emailSentConfirm ? "Email Sent!" : "Email Organizer"}
            </Button>
            <Button
              size="lg"
              color="primary"
              className="events-solid-button events-solid-button--danger"
              onPress={() => {
                if (cancelConfirm) {
                  navigate(backTo);
                } else {
                  setCancelConfirm(true);
                  setTimeout(() => setCancelConfirm(false), 3000);
                }
              }}
            >
              {cancelConfirm ? "Confirm Cancel?" : "Cancel Group Request"}
            </Button>
          </div>
        </header>

        <div className="events-detail-grid">
          <article className="events-surface">
            <h2 className="events-surface__title">Group Information</h2>
            <dl className="events-definition-list">
              <div>
                <dt>Type</dt>
                <dd>Corporate / {eventItem.groupType}</dd>
              </div>
              <div>
                <dt>Destination</dt>
                <dd>{eventItem.destination}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>{eventItem.organizerEmail}</dd>
              </div>
              <div>
                <dt>Organizer Name</dt>
                <dd>{eventItem.organizerName || "Not available"}</dd>
              </div>
            </dl>
          </article>

          <article className="events-surface">
            <h2 className="events-surface__title">Key Dates</h2>
            <dl className="events-definition-list">
              <div>
                <dt>Created</dt>
                <dd>Jan 12, 2025</dd>
              </div>
              <div>
                <dt>Last Modified</dt>
                <dd>Feb 8, 2025</dd>
              </div>
              <div>
                <dt>Event Start</dt>
                <dd>{formatShortDate(eventItem.departDate)}</dd>
              </div>
              <div>
                <dt>Event End</dt>
                <dd>{formatShortDate(eventItem.returnDate)}</dd>
              </div>
            </dl>
          </article>

          <article className="events-surface events-surface--metric-card">
            <h2 className="events-surface__title">Group Metrics</h2>
            <strong className="events-surface__metric">
              {eventItem.passengers}
            </strong>
            <span>Total Travelers</span>
            <span className="events-tone events-tone--info">
              {eventItem.bookedCount} booked
            </span>
          </article>
        </div>

        <article className="events-surface">
          <header className="events-page__hero events-page__hero--table">
            <h2 className="events-surface__title">
              Authorized Airlines for Booking
            </h2>
            <span className="events-table__caption">
              {
                eventItem.airlineAccess.filter(
                  (airline: { name: string; enabled: boolean }) =>
                    airlineState[airline.name] ?? airline.enabled,
                ).length
              }{" "}
              active
            </span>
          </header>
          <div className="events-airline-grid">
            {eventItem.airlineAccess.map(
              (airline: {
                name: string;
                enabled: boolean;
                bookingsLabel?: string;
              }) => (
                <div key={airline.name} className="events-airline-grid__item">
                  <div>
                    <strong>{airline.name}</strong>
                    {airline.bookingsLabel ? (
                      <p>{airline.bookingsLabel}</p>
                    ) : null}
                  </div>
                  <Toggle
                    aria-label={`Authorize ${airline.name}`}
                    isSelected={airlineState[airline.name] ?? airline.enabled}
                    onChange={(selected) =>
                      setAirlineState((prev) => ({
                        ...prev,
                        [airline.name]: selected,
                      }))
                    }
                    size="sm"
                    slim
                  />
                </div>
              ),
            )}
          </div>
        </article>

        <article className="events-surface events-surface--table">
          <header className="events-page__hero events-page__hero--table">
            <h2 className="events-surface__title">Passenger List</h2>
            <div className="events-page__hero-actions">
              <Button
                size="sm"
                color="secondary"
                className="events-outline-button"
                onPress={() => {
                  const csvRows = [
                    [
                      "Name",
                      "Number",
                      "Airline",
                      "Locator",
                      "Ticket",
                      "Amount Paid",
                    ].join(","),
                    ...eventItem.passengerManifest.map(
                      (p: {
                        name: string;
                        number: number;
                        airline: string;
                        locator: string;
                        ticket: string;
                        amountPaid: string;
                      }) =>
                        [
                          p.name,
                          p.number,
                          p.airline,
                          p.locator,
                          p.ticket,
                          p.amountPaid,
                        ].join(","),
                    ),
                  ];
                  const blob = new Blob([csvRows.join("\n")], {
                    type: "text/csv",
                  });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `${eventItem.id}-passenger-template.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                <DownloadIcon className="size-4" />
                Template
              </Button>
              <Button
                size="sm"
                color="secondary"
                className="events-outline-button"
                onPress={handleBulkUpload}
              >
                {uploadStatus ?? "Bulk Upload"}
              </Button>
              <Button
                size="sm"
                color="primary"
                className="events-pill-button"
                onPress={() => setIsAddPassengerOpen(true)}
              >
                <PlusIcon className="size-4" />
                Add Passenger
              </Button>
            </div>
          </header>

          <table className="events-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Number</th>
                <th>Airline</th>
                <th>Airline Locator</th>
                <th>Ticket</th>
                <th>Amount Paid</th>
              </tr>
            </thead>
            <tbody>
              {eventItem.passengerManifest.map(
                (passenger: {
                  id: string;
                  name: string;
                  number: number;
                  airline: string;
                  locator: string;
                  ticket: string;
                  amountPaid: string;
                }) => (
                  <tr key={passenger.id}>
                    <td>{passenger.name}</td>
                    <td>{passenger.number}</td>
                    <td>{passenger.airline}</td>
                    <td>{passenger.locator}</td>
                    <td>{passenger.ticket}</td>
                    <td>{passenger.amountPaid}</td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </article>

        <article className="events-surface">
          <header className="events-page__hero events-page__hero--table">
            <div>
              <h2 className="events-surface__title">Travel Agreements</h2>
              <p className="events-page__subtitle">
                Review and manage signed contracts for this group.
              </p>
            </div>
            <div className="events-page__hero-actions">
              <Button
                size="sm"
                color="secondary"
                className="events-outline-button"
                onPress={() => setAgreementGenerated(true)}
              >
                {agreementGenerated
                  ? "Agreement Generated"
                  : "Generate Agreement"}
              </Button>
              <Button
                size="sm"
                color="primary"
                className="events-pill-button"
                onPress={() => setSignatureSent(true)}
              >
                {signatureSent ? "Sent!" : "Send for Signature"}
              </Button>
            </div>
          </header>

          <div className="events-file-row">
            <div>
              <strong>{eventItem.agreement?.fileName}</strong>
              <p>{eventItem.agreement?.uploadedAt}</p>
            </div>
            <div className="events-page__hero-actions">
              <span className="events-tone events-tone--success">
                {eventItem.agreement?.status}
              </span>
              <DownloadIcon className="size-4" />
            </div>
          </div>
        </article>
      </div>

      <ModalOverlay isOpen={isEditGroupOpen} onOpenChange={setIsEditGroupOpen}>
        <Modal className="events-modal events-modal--compact">
          <Dialog className="events-dialog" aria-label="Edit group details">
            <div className="events-modal__header">
              <h3>Edit Group</h3>
              <button
                type="button"
                className="events-dialog__close"
                onClick={() => setIsEditGroupOpen(false)}
                aria-label="Close modal"
              >
                <CloseIcon className="size-4" />
              </button>
            </div>

            <ValidatedForm
              form={editGroupForm}
              onSubmit={handleEditGroup}
              className="events-form-stack"
            >
              <ValidatedInput
                form={editGroupForm}
                name="groupName"
                label="Group Name"
                placeholder="Enter group name"
              />

              <Controller
                control={editGroupForm.control}
                name="groupType"
                render={({ field }) => (
                  <div className="events-form-row events-form-row--full">
                    <label
                      className="events-form-label"
                      htmlFor="edit-group-type"
                    >
                      Group Type
                    </label>
                    <Select
                      id="edit-group-type"
                      selectedKey={field.value}
                      onSelectionChange={(key) => field.onChange(String(key))}
                      items={[
                        { id: "Corporate", label: "Corporate" },
                        { id: "Wedding", label: "Wedding" },
                        { id: "Sports", label: "Sports" },
                        { id: "Summit", label: "Summit" },
                        { id: "Conference", label: "Conference" },
                      ]}
                      placeholder="Select type"
                    >
                      {(item) => (
                        <Select.Item id={item.id} textValue={item.label}>
                          {item.label}
                        </Select.Item>
                      )}
                    </Select>
                  </div>
                )}
              />

              <div className="events-form-row">
                <ValidatedInput
                  form={editGroupForm}
                  name="organizerName"
                  label="Organizer Name"
                  placeholder="Organizer name"
                />
                <ValidatedInput
                  form={editGroupForm}
                  name="organizerEmail"
                  label="Organizer Email"
                  placeholder="organizer@company.com"
                  type="email"
                />
              </div>

              <ValidatedInput
                form={editGroupForm}
                name="destination"
                label="Destination"
                placeholder="City, Country"
              />

              <div className="events-form-row">
                <ValidatedInput
                  form={editGroupForm}
                  name="venueName"
                  label="Venue Name"
                  placeholder="Venue name"
                />
                <Controller
                  control={editGroupForm.control}
                  name="venueAddress"
                  render={({ field, fieldState }) => (
                    <label className="events-form-label">
                      Venue Address
                      <PlacesAutocompleteInput
                        {...field}
                        ref={field.ref}
                        aria-label="Venue address"
                        placeholder="Full address"
                        isInvalid={fieldState.invalid}
                        onPlaceSelect={({ address }) => {
                          editGroupForm.setValue("venueAddress", address, {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: true,
                          });
                        }}
                      />
                      {fieldState.error ? (
                        <span className="events-form-error">
                          {fieldState.error.message}
                        </span>
                      ) : null}
                    </label>
                  )}
                />
              </div>

              <div className="events-form-row">
                <Controller
                  control={editGroupForm.control}
                  name="departDate"
                  render={({ field }) => (
                    <label className="events-form-label">
                      Depart Date
                      <InputBase
                        aria-label="Departure date"
                        type="date"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </label>
                  )}
                />
                <Controller
                  control={editGroupForm.control}
                  name="returnDate"
                  render={({ field }) => (
                    <label className="events-form-label">
                      Return Date
                      <InputBase
                        aria-label="Return date"
                        type="date"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </label>
                  )}
                />
              </div>

              <div className="events-form-row">
                <Controller
                  control={editGroupForm.control}
                  name="passengers"
                  render={({ field }) => (
                    <label className="events-form-label">
                      Passengers
                      <InputBase
                        aria-label="Number of passengers"
                        type="number"
                        value={String(field.value)}
                        onChange={(v) => field.onChange(Number(v))}
                      />
                    </label>
                  )}
                />
                <Controller
                  control={editGroupForm.control}
                  name="paymentModel"
                  render={({ field }) => (
                    <div className="events-form-row events-form-row--full">
                      <label
                        className="events-form-label"
                        htmlFor="edit-payment-model"
                      >
                        Payment Model
                      </label>
                      <Select
                        id="edit-payment-model"
                        selectedKey={field.value}
                        onSelectionChange={(key) => field.onChange(String(key))}
                        items={[
                          { id: "Organizer Pays", label: "Organizer Pays" },
                          { id: "Traveler Pays", label: "Traveler Pays" },
                        ]}
                        placeholder="Select model"
                      >
                        {(item) => (
                          <Select.Item id={item.id} textValue={item.label}>
                            {item.label}
                          </Select.Item>
                        )}
                      </Select>
                    </div>
                  )}
                />
              </div>

              <div className="events-modal__footer">
                <Button
                  size="lg"
                  color="primary"
                  className="events-solid-button"
                  type="submit"
                >
                  Save Changes
                </Button>
                <Button
                  size="lg"
                  color="secondary"
                  className="events-outline-button"
                  onPress={() => setIsEditGroupOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </ValidatedForm>
          </Dialog>
        </Modal>
      </ModalOverlay>

      <ModalOverlay
        isOpen={isAddPassengerOpen}
        onOpenChange={setIsAddPassengerOpen}
      >
        <Modal className="events-modal events-modal--compact">
          <Dialog className="events-dialog" aria-label="Add passenger">
            <div className="events-modal__header">
              <h3>Add Passenger</h3>
              <button
                type="button"
                className="events-dialog__close"
                onClick={() => setIsAddPassengerOpen(false)}
                aria-label="Close modal"
              >
                <CloseIcon className="size-4" />
              </button>
            </div>

            <ValidatedForm
              form={addPassengerForm}
              onSubmit={handleAddPassenger}
              className="events-form-stack"
            >
              <ValidatedInput
                form={addPassengerForm}
                name="name"
                label="Passenger Name"
                placeholder="Last, First"
              />

              <div className="events-form-row">
                <ValidatedInput
                  form={addPassengerForm}
                  name="airline"
                  label="Airline Code"
                  placeholder="AA"
                />
                <ValidatedInput
                  form={addPassengerForm}
                  name="locator"
                  label="Airline Locator"
                  placeholder="ABC123"
                />
              </div>

              <div className="events-form-row">
                <ValidatedInput
                  form={addPassengerForm}
                  name="ticket"
                  label="Ticket Number"
                  placeholder="00123984210"
                />
                <ValidatedInput
                  form={addPassengerForm}
                  name="amountPaid"
                  label="Amount Paid"
                  placeholder="$734.87 USD"
                />
              </div>

              <div className="events-modal__footer">
                <Button
                  size="lg"
                  color="primary"
                  className="events-solid-button"
                  type="submit"
                >
                  Add Passenger
                </Button>
                <Button
                  size="lg"
                  color="secondary"
                  className="events-outline-button"
                  onPress={() => setIsAddPassengerOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </ValidatedForm>
          </Dialog>
        </Modal>
      </ModalOverlay>

      <ModalOverlay
        isOpen={isEmailOrganizerOpen}
        onOpenChange={setIsEmailOrganizerOpen}
      >
        <Modal className="events-modal events-modal--compact">
          <Dialog className="events-dialog" aria-label="Email organizer">
            <div className="events-modal__header">
              <h3>Email Organizer</h3>
              <button
                type="button"
                className="events-dialog__close"
                onClick={() => setIsEmailOrganizerOpen(false)}
                aria-label="Close modal"
              >
                <CloseIcon className="size-4" />
              </button>
            </div>

            <ValidatedForm
              form={emailOrganizerForm}
              onSubmit={handleEmailOrganizer}
              className="events-form-stack"
            >
              <ValidatedInput
                form={emailOrganizerForm}
                name="to"
                label="To"
                type="email"
                placeholder="organizer@company.com"
              />
              <ValidatedInput
                form={emailOrganizerForm}
                name="subject"
                label="Subject"
                placeholder="Email subject"
              />

              <Controller
                control={emailOrganizerForm.control}
                name="body"
                render={({ field, fieldState }) => (
                  <label className="events-form-label">
                    Message
                    <TextAreaBase
                      rows={6}
                      value={field.value}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      placeholder="Type your message to the organizer..."
                      className={
                        fieldState.invalid ? "ring-error_subtle" : undefined
                      }
                    />
                    {fieldState.error ? (
                      <span className="events-form-error">
                        {fieldState.error.message}
                      </span>
                    ) : null}
                  </label>
                )}
              />

              <div className="events-modal__footer">
                <Button
                  size="lg"
                  color="primary"
                  className="events-solid-button"
                  type="submit"
                >
                  Send Email
                </Button>
                <Button
                  size="lg"
                  color="secondary"
                  className="events-outline-button"
                  onPress={() => setIsEmailOrganizerOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </ValidatedForm>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </section>
  );
}
